import { Data, Match } from 'effect';
import { max, min } from 'lodash';
import { AsOfDateSetting, assert, assertNotNil, FormattedDateString } from 'venn-utils';
import moment from 'moment';

type ThirtyDayCalendarActions = Data.TaggedEnum<{
  ChooseYear: { year: number };
  ChooseMonth: { month: number };
  ChoosePreviousMonth: {};
  ChooseNextMonth: {};
  ChooseDay: { dateTimestamp: number };
  ChooseCurrentAllocationAsOfDate: {};
  ChooseLastUploadedAllocationAsOfDate: {};
}>;
export const ThirtyDayCalendarActions = Data.taggedEnum<ThirtyDayCalendarActions>();

type Year = number;
type Month = number;
type Dependencies = {
  state: {
    yearToMonthsWithAllocations: Record<Year, Month[]>;
    selectedYear: number;
    selectedMonth: number;
  };
  mutations: {
    setSelectedYear: (year: number) => void;
    setSelectedMonth: (month: number) => void;
    setAllocationsAsOfDate: (asOfDate: AsOfDateSetting) => void;
    closeDropdownPanel: () => void;
  };
};
export const useThirtyDayCalendarActions =
  ({
    state: { yearToMonthsWithAllocations, selectedYear, selectedMonth },
    mutations: { setSelectedMonth, setSelectedYear, setAllocationsAsOfDate, closeDropdownPanel },
  }: Dependencies) =>
  (action: ThirtyDayCalendarActions) =>
    Match.value(action).pipe(
      Match.tag('ChooseYear', ({ year }) => {
        setSelectedYear(year);
        setSelectedMonth(assertNotNil(min(assertNotNil(yearToMonthsWithAllocations[year]))));
      }),
      Match.tag('ChooseMonth', ({ month }) => setSelectedMonth(month)),
      Match.tag('ChoosePreviousMonth', () => {
        const firstMonth = assertNotNil(
          min(yearToMonthsWithAllocations[assertNotNil(min(Object.keys(yearToMonthsWithAllocations).map(Number)))]),
        );
        const currentMonth = moment.utc().year(selectedYear).month(selectedMonth);
        assert(!(currentMonth.isSame(firstMonth, 'month') && currentMonth.isSame(firstMonth, 'year')));
        if (selectedMonth === 0) {
          setSelectedYear(selectedYear - 1);
          setSelectedMonth(11);
        } else {
          setSelectedMonth(selectedMonth - 1);
        }
      }),
      Match.tag('ChooseNextMonth', () => {
        const lastMonth = assertNotNil(
          max(yearToMonthsWithAllocations[assertNotNil(max(Object.keys(yearToMonthsWithAllocations).map(Number)))]),
        );
        const currentMonth = moment.utc().year(selectedYear).month(selectedMonth);
        assert(!(currentMonth.isSame(lastMonth, 'month') && currentMonth.isSame(lastMonth, 'year')));
        if (selectedMonth === 11) {
          setSelectedYear(selectedYear + 1);
          setSelectedMonth(0);
        } else {
          setSelectedMonth(selectedMonth + 1);
        }
      }),
      Match.tag('ChooseDay', ({ dateTimestamp }) => {
        setAllocationsAsOfDate(
          AsOfDateSetting.Date({ date: FormattedDateString(moment.utc(dateTimestamp).format('MM/DD/YYYY')) }),
        );
        closeDropdownPanel();
      }),
      Match.tag('ChooseCurrentAllocationAsOfDate', () => {
        setAllocationsAsOfDate(AsOfDateSetting.Current());
        closeDropdownPanel();
      }),
      Match.tag('ChooseLastUploadedAllocationAsOfDate', () => {
        setAllocationsAsOfDate(AsOfDateSetting.LastUploaded());
        closeDropdownPanel();
      }),
      Match.exhaustive,
    );
